import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FileText, Image, Video, Filter, Search, Download, Eye, Loader2,
  Grid, List, Send, Clock, Trash2, RefreshCw, AlertCircle, Upload, Plus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import TikTokPostModal from "../components/dashboard/TikTokPostModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContentLibrary() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [allContent, setAllContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(null); // Track which content is being posted
  const [deleting, setDeleting] = useState(null); // Track which content is being deleted
  const [regenerating, setRegenerating] = useState(null); // Track which content is regenerating
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterCampaign, setFilterCampaign] = useState("all");
  
  // Helper to get user ID from JWT token
  const getUserId = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub;
      } catch (e) { return null; }
    }
    return null;
  };
  const [viewMode, setViewMode] = useState("grid");
  const [selectedPostPlatform, setSelectedPostPlatform] = useState(null); // For "Post to..." feature
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploadCampaignId, setUploadCampaignId] = useState("");
  const fileInputRef = useRef(null);
  
  // TikTok Post Modal state
  const [tiktokModalOpen, setTiktokModalOpen] = useState(false);
  const [tiktokPostContent, setTiktokPostContent] = useState(null);
  
  // Content type selector for Instagram/Facebook (reel vs story)
  const [selectedContentType, setSelectedContentType] = useState('reel');

  const availablePlatforms = [
    { id: 'all', name: 'All Platforms', emoji: '🌐' },
    { id: 'twitter', name: 'Twitter/X', emoji: '🐦' },
    { id: 'linkedin', name: 'LinkedIn', emoji: '💼' },
    { id: 'tiktok', name: 'TikTok', emoji: '🎵' },
    { id: 'youtube', name: 'YouTube', emoji: '▶️' },
    { id: 'instagram', name: 'Instagram', emoji: '📸' },
    { id: 'facebook', name: 'Facebook', emoji: '📘' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterContent();
  }, [searchTerm, filterType, filterCampaign, allContent]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const campaignsRes = await axios.get(`${API}/campaigns/`, { headers });
      setCampaigns(campaignsRes.data || []);
      
      // Fetch content for all campaigns
      const contentPromises = (campaignsRes.data || []).map(c => 
        axios.get(`${API}/campaigns/${c.id}/content`, { headers }).catch(() => ({ data: [] }))
      );
      const contentResults = await Promise.all(contentPromises);
      
      const combinedContent = contentResults.flatMap((res, i) => 
        (res.data || []).map(item => ({
          ...item,
          campaign_name: campaignsRes.data[i]?.name || 'Unknown Campaign'
        }))
      );
      
      setAllContent(combinedContent);
      setFilteredContent(combinedContent);
    } catch (error) {
      console.error("Error fetching content:", error);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const postContent = async (item, targetPlatform = null, contentType = 'reel') => {
    const platform = targetPlatform || item.platforms?.[0] || item.platform || 'tiktok';
    
    // For TikTok, show the TikTok Post Modal (required by TikTok UX guidelines)
    if (platform === 'tiktok') {
      setTiktokPostContent(item);
      setTiktokModalOpen(true);
      setSelectedPostPlatform(null);
      return;
    }
    
    // Handle "Post to All Platforms"
    if (platform === 'all') {
      const platformsToPost = availablePlatforms.filter(p => p.id !== 'all').map(p => p.id);
      
      // For TikTok in "all platforms", we need to show the modal separately
      const hasTikTok = platformsToPost.includes('tiktok');
      const otherPlatforms = platformsToPost.filter(p => p !== 'tiktok');
      
      if (hasTikTok) {
        toast.info('TikTok requires manual confirmation. Opening TikTok post dialog...');
        setTiktokPostContent(item);
        setTiktokModalOpen(true);
      }
      
      if (otherPlatforms.length === 0) {
        setSelectedPostPlatform(null);
        return;
      }
      
      if (!confirm(`Post this content to ${otherPlatforms.length} platform(s) (excluding TikTok)?`)) return;
      
      setPosting(item.id);
      const results = [];
      
      for (const p of otherPlatforms) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${API}/social/post`, {
            content_id: item.id,
            platform: p,
            text: item.caption || item.content || item.script || '',
            campaign_id: item.campaign_id,
            content_type: (p === 'instagram' || p === 'facebook') ? contentType : 'post'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.success) {
            results.push({ platform: p, success: true });
          } else {
            results.push({ platform: p, success: false, error: response.data.error });
          }
        } catch (error) {
          results.push({ platform: p, success: false, error: error.message });
        }
      }
      
      const successes = results.filter(r => r.success).length;
      const failures = results.filter(r => !r.success);
      
      if (successes > 0) {
        toast.success(`Posted to ${successes} platform(s)!`);
      }
      if (failures.length > 0) {
        toast.error(`Failed on ${failures.length} platform(s): ${failures.map(f => f.platform).join(', ')}`);
      }
      
      setPosting(null);
      setSelectedPostPlatform(null);
      fetchData();
      return;
    }
    
    // Determine content type label for confirmation
    const contentTypeLabel = (platform === 'instagram' || platform === 'facebook') 
      ? (contentType === 'story' ? 'Story' : 'Reel/Post')
      : '';
    const confirmMsg = contentTypeLabel 
      ? `Post this content as a ${contentTypeLabel} to ${platform.toUpperCase()}?`
      : `Post this content to ${platform.toUpperCase()}?`;
    
    if (!confirm(confirmMsg)) return;
    
    setPosting(item.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/social/post`, {
        content_id: item.id,
        platform: platform,
        text: item.caption || item.content || item.script || '',
        campaign_id: item.campaign_id,
        content_type: (platform === 'instagram' || platform === 'facebook') ? contentType : 'post'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Posted to ${platform}!`);
        // Refresh content list
        fetchData();
      } else {
        // Show the actual error from the API
        const errorMsg = response.data.error || response.data.detail || 'Post failed';
        toast.error(`${platform.toUpperCase()}: ${errorMsg}`);
      }
    } catch (error) {
      console.error("Post error:", error);
      // Get error from response data if available
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || error.message || `Failed to post to ${platform}`;
      toast.error(errorMsg);
    } finally {
      setPosting(null);
      setSelectedPostPlatform(null);
    }
  };

  const deleteContent = async (item) => {
    setDeleting(item.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/campaigns/${item.campaign_id}/content/${item.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success("Content deleted successfully");
        // Remove from local state
        setAllContent(prev => prev.filter(c => c.id !== item.id));
      } else {
        throw new Error(response.data.error || 'Delete failed');
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to delete content");
    } finally {
      setDeleting(null);
    }
  };

  const regenerateContent = async (item, notes = "") => {
    setRegenerating(item.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/campaigns/${item.campaign_id}/content/${item.id}/regenerate`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success("Content regeneration started! This may take a minute...");
        // Update status in local state
        setAllContent(prev => prev.map(c => 
          c.id === item.id ? { ...c, status: 'regenerating' } : c
        ));
        // Refresh after a delay
        setTimeout(() => fetchData(), 30000);
      } else {
        throw new Error(response.data.error || 'Regeneration failed');
      }
    } catch (error) {
      console.error("Regenerate error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to start regeneration");
    } finally {
      setRegenerating(null);
    }
  };

  const deleteCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/campaigns/${campaignId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Campaign deleted (${response.data.content_deleted} content pieces removed)`);
        fetchData(); // Refresh
      } else {
        throw new Error(response.data.error || 'Delete failed');
      }
    } catch (error) {
      console.error("Campaign delete error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to delete campaign");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 50MB.");
      return;
    }
    
    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error("Please select an image or video file.");
      return;
    }
    
    setUploadFile(file);
  };

  const uploadContent = async () => {
    if (!uploadFile) {
      toast.error("Please select a file to upload.");
      return;
    }
    
    if (!uploadCampaignId) {
      toast.error("Please select a campaign.");
      return;
    }
    
    setUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('campaign_id', uploadCampaignId);
      formData.append('caption', uploadCaption);
      
      const response = await axios.post(
        `${API}/content/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        toast.success("Content uploaded successfully!");
        setUploadModalOpen(false);
        setUploadFile(null);
        setUploadCaption("");
        setUploadCampaignId("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        fetchData(); // Refresh content list
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to upload content");
    } finally {
      setUploading(false);
    }
  };

  const filterContent = () => {
    let filtered = [...allContent];
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterType !== "all") {
      filtered = filtered.filter(item => item.content_type === filterType);
    }
    
    if (filterCampaign !== "all") {
      filtered = filtered.filter(item => item.campaign_id === filterCampaign);
    }
    
    setFilteredContent(filtered);
  };

  const getContentIcon = (type, hasImage, hasVideo) => {
    if (hasVideo) return <Video className="w-4 h-4 text-red-400" />;
    if (hasImage) return <Image className="w-4 h-4 text-purple-400" />;
    switch (type) {
      case 'script': return <FileText className="w-4 h-4 text-blue-400" />;
      case 'image': return <Image className="w-4 h-4 text-purple-400" />;
      case 'video': 
      case 'short_video':
      case 'reels':
        return <Video className="w-4 h-4 text-red-400" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getImageSrc = (item) => {
    // Check for file path (large files stored on disk)
    if (item.image_path) {
      // Extract filename from path
      const filename = item.image_path.split('/').pop();
      return `${API}/uploads/${filename}`;
    }
    // Check for base64 field
    if (item.image_base64) {
      return `data:image/png;base64,${item.image_base64}`;
    }
    if (item.content_type === 'image' && item.content) {
      return `data:image/png;base64,${item.content}`;
    }
    return null;
  };

  const getVideoSrc = (item) => {
    // Check for file path (large files stored on disk)
    if (item.video_path) {
      // Extract filename from path
      const filename = item.video_path.split('/').pop();
      return `${API}/uploads/${filename}`;
    }
    if (item.video_base64) {
      return `data:video/mp4;base64,${item.video_base64}`;
    }
    return null;
  };

  const hasVisualContent = (item) => {
    return item.image_base64 || item.image_path || item.has_image || item.video_base64 || item.video_path || item.has_video || item.content_type === 'image';
  };

  const hasVideoContent = (item) => {
    return item.video_base64 || item.video_path || item.has_video;
  };

  const downloadContent = (item) => {
    const videoSrc = getVideoSrc(item);
    const imageSrc = getImageSrc(item);
    
    if (videoSrc) {
      const link = document.createElement('a');
      link.href = videoSrc;
      link.download = `${item.title || 'content'}.mp4`;
      link.click();
      toast.success("Video downloaded!");
    } else if (imageSrc) {
      const link = document.createElement('a');
      link.href = imageSrc;
      link.download = `${item.title || 'content'}.png`;
      link.click();
      toast.success("Image downloaded!");
    } else {
      const textContent = item.script || item.caption || item.content || '';
      const blob = new Blob([textContent], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${item.title || 'content'}.txt`;
      link.click();
      toast.success("Script downloaded!");
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <Sidebar active="content" />
      
      <main className="flex-1 md:ml-64 p-6 lg:p-8 overflow-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Content Library</h1>
            <p className="text-muted-foreground mt-1">All your generated marketing content</p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Upload Content Button */}
            <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-primary hover:bg-primary/90"
                  data-testid="upload-content-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Content
                </Button>
              </DialogTrigger>
              <DialogContent className="glass border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Upload Your Content
                  </DialogTitle>
                  <DialogDescription>
                    Upload your own images or videos to add to a campaign
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  {/* Campaign Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="upload-campaign">Select Campaign *</Label>
                    <Select value={uploadCampaignId} onValueChange={setUploadCampaignId}>
                      <SelectTrigger className="w-full bg-white/5 border-white/10" data-testid="upload-campaign-select">
                        <SelectValue placeholder="Choose a campaign" />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10">
                        {campaigns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {campaigns.length === 0 && (
                      <p className="text-xs text-yellow-400">
                        No campaigns found. Create a campaign first from the Dashboard.
                      </p>
                    )}
                  </div>
                  
                  {/* File Upload */}
                  <div className="space-y-2">
                    <Label htmlFor="upload-file">Image or Video File *</Label>
                    <div className="relative">
                      <Input
                        ref={fileInputRef}
                        id="upload-file"
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleFileSelect}
                        className="bg-white/5 border-white/10"
                        data-testid="upload-file-input"
                      />
                    </div>
                    {uploadFile && (
                      <div className="flex items-center gap-2 text-sm text-green-400">
                        {uploadFile.type.startsWith('image/') ? (
                          <Image className="w-4 h-4" />
                        ) : (
                          <Video className="w-4 h-4" />
                        )}
                        <span>{uploadFile.name}</span>
                        <span className="text-muted-foreground">
                          ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Supports: JPG, PNG, WebP, MP4, MOV, WebM (Max 50MB)
                    </p>
                  </div>
                  
                  {/* Caption */}
                  <div className="space-y-2">
                    <Label htmlFor="upload-caption">Caption (optional)</Label>
                    <Textarea
                      id="upload-caption"
                      value={uploadCaption}
                      onChange={(e) => setUploadCaption(e.target.value)}
                      placeholder="Enter a caption for your content..."
                      className="bg-white/5 border-white/10 min-h-[80px]"
                      data-testid="upload-caption-input"
                    />
                  </div>
                  
                  {/* Upload Button */}
                  <Button
                    onClick={uploadContent}
                    disabled={!uploadFile || !uploadCampaignId || uploading}
                    className="w-full bg-primary hover:bg-primary/90"
                    data-testid="upload-submit-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Add to Campaign
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
              data-testid="grid-view-btn"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10"
              data-testid="search-input"
            />
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px] bg-white/5 border-white/10" data-testid="filter-type">
              <SelectValue placeholder="Content Type" />
            </SelectTrigger>
            <SelectContent className="glass border-white/10">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="script">Scripts</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="video">Videos</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filterCampaign} onValueChange={setFilterCampaign}>
            <SelectTrigger className="w-[200px] bg-white/5 border-white/10" data-testid="filter-campaign">
              <SelectValue placeholder="Campaign" />
            </SelectTrigger>
            <SelectContent className="glass border-white/10">
              <SelectItem value="all">All Campaigns</SelectItem>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Content Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredContent.length === 0 ? (
          <Card className="glass-card border-white/5 border-dashed" data-testid="empty-content">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Content Found</h3>
              <p className="text-muted-foreground mb-6">
                {allContent.length === 0 
                  ? "Generate content from your campaigns to see it here"
                  : "Try adjusting your filters"}
              </p>
              <Button onClick={() => navigate("/dashboard")} data-testid="go-to-campaigns">
                Go to Campaigns
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredContent.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <Dialog>
                  <DialogTrigger asChild>
                    <Card className="glass-card border-white/5 card-hover cursor-pointer h-full" data-testid={`content-card-${i}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {getContentIcon(item.content_type, hasVisualContent(item), hasVideoContent(item))}
                            <Badge variant="outline" className="text-xs border-white/10">
                              {item.platforms?.length > 1 
                                ? `${item.platforms.length} platforms` 
                                : (item.platform || item.platform_variant || (item.platforms?.[0]) || 'tiktok')}
                            </Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadContent(item);
                            }}
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        {hasVideoContent(item) ? (
                          <div className="aspect-square bg-white/5 rounded-lg overflow-hidden mb-3 relative">
                            <video 
                              src={getVideoSrc(item)}
                              className="w-full h-full object-cover"
                              muted
                              loop
                              playsInline
                              onMouseEnter={(e) => e.target.play()}
                              onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                                <Video className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          </div>
                        ) : hasVisualContent(item) ? (
                          <div className="aspect-square bg-white/5 rounded-lg overflow-hidden mb-3">
                            <img 
                              src={getImageSrc(item)}
                              alt={item.title || item.topic || 'Content'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="aspect-square bg-white/5 rounded-lg p-3 mb-3 overflow-hidden">
                            <p className="text-xs text-muted-foreground line-clamp-6">
                              {item.script || item.caption || item.content || 'No content preview'}
                            </p>
                          </div>
                        )}
                        
                        <h4 className="font-medium text-sm truncate">{item.title || item.topic || item.hook || 'Untitled'}</h4>
                        <p className="text-xs text-muted-foreground truncate">{item.campaign_name}</p>
                        
                        {/* Show content type indicator */}
                        <div className="flex items-center gap-1 mt-2">
                          {hasVideoContent(item) && (
                            <Badge variant="outline" className="text-xs border-red-500/30 text-red-400">
                              <Video className="w-3 h-3 mr-1" />
                              AI Video
                            </Badge>
                          )}
                          {hasVisualContent(item) && !hasVideoContent(item) && (
                            <Badge variant="outline" className="text-xs border-green-500/30 text-green-400">
                              <Image className="w-3 h-3 mr-1" />
                              AI Image
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </DialogTrigger>
                  <DialogContent 
                    className="glass border-white/10 max-w-2xl max-h-[85vh] flex flex-col"
                    onPointerDownOutside={(e) => {
                      // Prevent closing when clicking on Select dropdowns (rendered in portal)
                      const target = e.target;
                      if (target?.closest('[role="listbox"]') || target?.closest('[data-radix-select-viewport]')) {
                        e.preventDefault();
                      }
                    }}
                    onInteractOutside={(e) => {
                      // Prevent closing when interacting with Select dropdowns
                      const target = e.target;
                      if (target?.closest('[role="listbox"]') || target?.closest('[data-radix-select-viewport]')) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <DialogHeader>
                      <DialogTitle>{item.title || item.topic || item.hook || 'Content'}</DialogTitle>
                      <DialogDescription>
                        {item.content_type} for {item.platforms?.length > 1 
                          ? `${item.platforms.length} platforms (${item.platforms.slice(0, 3).join(', ')}${item.platforms.length > 3 ? '...' : ''})` 
                          : (item.platform || item.platform_variant || (item.platforms?.[0]) || 'tiktok')} • {item.campaign_name}
                      </DialogDescription>
                    </DialogHeader>
                    
                    {/* Scrollable content area */}
                    <div className="flex-1 overflow-y-auto mt-4 space-y-4 pr-2">
                      {/* Video Preview */}
                      {hasVideoContent(item) && (
                        <div className="rounded-lg overflow-hidden bg-black">
                          <video 
                            src={getVideoSrc(item)}
                            className="w-full rounded-lg"
                            controls
                            playsInline
                          />
                        </div>
                      )}
                      
                      {/* Image Preview (thumbnail if video exists) */}
                      {hasVisualContent(item) && !hasVideoContent(item) && (
                        <div className="rounded-lg overflow-hidden">
                          <img 
                            src={getImageSrc(item)}
                            alt={item.title || item.topic || 'Content'}
                            className="w-full rounded-lg"
                          />
                        </div>
                      )}
                      
                      {/* Thumbnail for videos */}
                      {hasVideoContent(item) && hasVisualContent(item) && item.image_base64 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-2">Thumbnail:</p>
                          <img 
                            src={getImageSrc(item)}
                            alt="Thumbnail"
                            className="w-32 h-32 object-cover rounded-lg"
                          />
                        </div>
                      )}
                      
                      {/* Script/Caption Content */}
                      {(item.script || item.caption || item.content) && (
                        <div>
                          {item.hook && (
                            <div className="mb-3 p-3 rounded-lg bg-primary/10 border border-primary/30">
                              <p className="text-xs text-primary mb-1 font-medium">Hook:</p>
                              <p className="text-sm">{item.hook}</p>
                            </div>
                          )}
                          <ScrollArea className="h-[200px] rounded-lg bg-white/5 p-4">
                            <pre className="whitespace-pre-wrap text-sm">{item.script || item.caption || item.content}</pre>
                          </ScrollArea>
                        </div>
                      )}
                      
                      {/* Hashtags */}
                      {item.hashtags && item.hashtags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {item.hashtags.map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="border-primary/30 text-primary">
                              #{tag.replace('#', '')}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Fixed footer with buttons - always visible */}
                    <div className="border-t border-white/10 pt-4 mt-4 space-y-3">
                      {/* Status badges */}
                      <div className="flex gap-2 flex-wrap">
                        <Badge variant="outline" className={`
                          ${item.status === 'published' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 
                            item.status === 'scheduled' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                            item.status === 'regenerating' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                            'bg-gray-500/20 text-gray-400 border-gray-500/30'}
                        `}>
                          {item.status === 'regenerating' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                          {item.status || 'draft'}
                        </Badge>
                        {item.scheduled_at && (
                          <Badge variant="outline" className="border-white/10">
                            <Clock className="w-3 h-3 mr-1" />
                            {new Date(item.scheduled_at).toLocaleDateString()}
                          </Badge>
                        )}
                        {hasVideoContent(item) && (
                          <Badge variant="outline" className="border-red-500/30 text-red-400">
                            <Video className="w-3 h-3 mr-1" />
                            AI Video
                          </Badge>
                        )}
                        {hasVisualContent(item) && !hasVideoContent(item) && (
                          <Badge variant="outline" className="border-green-500/30 text-green-400">
                            <Image className="w-3 h-3 mr-1" />
                            AI Image
                          </Badge>
                        )}
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2 justify-between" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          {/* Delete Button */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                disabled={deleting === item.id}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {deleting === item.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4 mr-1" />
                                )}
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="glass border-white/10">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Content?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this content piece. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-white/10">Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteContent(item);
                                  }}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          
                          {/* Regenerate Button */}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              regenerateContent(item);
                            }}
                            disabled={regenerating === item.id || item.status === 'regenerating'}
                            className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                            title="Regenerate with better images"
                          >
                            {regenerating === item.id || item.status === 'regenerating' ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-1" />
                            )}
                            Regenerate
                          </Button>
                          
                          <Button 
                            variant="outline"
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadContent(item);
                            }}
                            className="border-white/20"
                          >
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                        </div>
                        
                        {/* Always show posting buttons - allow re-posting to multiple platforms */}
                        <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                          <Select 
                            value={selectedPostPlatform || ''} 
                            onValueChange={(platform) => {
                              setSelectedPostPlatform(platform);
                              // Reset content type when platform changes
                              if (platform === 'instagram' || platform === 'facebook') {
                                setSelectedContentType('reel');
                              }
                            }}
                          >
                            <SelectTrigger 
                              className="w-[160px] h-9 border-white/20" 
                              data-testid="platform-selector"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SelectValue placeholder="Select platform" />
                            </SelectTrigger>
                            <SelectContent onPointerDownCapture={(e) => e.stopPropagation()}>
                              {availablePlatforms.map(p => (
                                <SelectItem key={p.id} value={p.id} data-testid={`platform-option-${p.id}`}>
                                  {p.emoji} {p.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          {/* Show Reel/Story selector for Instagram and Facebook */}
                          {(selectedPostPlatform === 'instagram' || selectedPostPlatform === 'facebook') && (
                            <Select 
                              value={selectedContentType} 
                              onValueChange={setSelectedContentType}
                            >
                              <SelectTrigger 
                                className="w-[120px] h-9 border-white/20" 
                                data-testid="content-type-selector"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent onPointerDownCapture={(e) => e.stopPropagation()}>
                                <SelectItem value="reel" data-testid="content-type-reel">
                                  🎬 Reel
                                </SelectItem>
                                <SelectItem value="story" data-testid="content-type-story">
                                  📖 Story
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          
                          <Button 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Require platform selection before posting
                              if (!selectedPostPlatform) {
                                toast.warning('Please select a platform first');
                                return;
                              }
                              postContent(item, selectedPostPlatform, selectedContentType);
                            }}
                            disabled={posting === item.id}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="post-now-btn"
                          >
                            {posting === item.id ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-1" />
                            )}
                            Post Now
                          </Button>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredContent.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02 }}
              >
                <Dialog>
                  <DialogTrigger asChild>
                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-primary/30 cursor-pointer transition-colors" data-testid={`content-row-${i}`}>
                      {getContentIcon(item.content_type, hasVisualContent(item))}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.title || item.topic || item.hook || 'Untitled'}</p>
                        <p className="text-sm text-muted-foreground truncate">{item.campaign_name}</p>
                      </div>
                      {hasVisualContent(item) && (
                        <Badge variant="outline" className="border-green-500/30 text-green-400 hidden sm:inline-flex">
                          <Image className="w-3 h-3 mr-1" />
                          AI Image
                        </Badge>
                      )}
                      <Badge variant="outline" className="border-white/10 hidden sm:inline-flex">
                        {item.platform || item.platform_variant || (item.platforms?.[0]) || 'tiktok'}
                      </Badge>
                      <Badge variant="outline" className="border-white/10">
                        {item.content_type}
                      </Badge>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => {
                        e.stopPropagation();
                        downloadContent(item);
                      }}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="glass border-white/10 max-w-2xl max-h-[80vh] overflow-auto">
                    <DialogHeader>
                      <DialogTitle>{item.title || item.topic || item.hook || 'Content'}</DialogTitle>
                      <DialogDescription>
                        {item.content_type} for {item.platform || item.platform_variant || (item.platforms?.[0]) || 'tiktok'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4 space-y-4">
                      {hasVisualContent(item) && (
                        <img 
                          src={getImageSrc(item)}
                          alt={item.title || item.topic || 'Content'}
                          className="w-full rounded-lg"
                        />
                      )}
                      {(item.script || item.caption || item.content) && (
                        <ScrollArea className="h-[300px] rounded-lg bg-white/5 p-4">
                          <pre className="whitespace-pre-wrap text-sm">{item.script || item.caption || item.content}</pre>
                        </ScrollArea>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      
      {/* TikTok Post Modal - Required by TikTok UX Guidelines */}
      <TikTokPostModal
        isOpen={tiktokModalOpen}
        onClose={() => {
          setTiktokModalOpen(false);
          setTiktokPostContent(null);
        }}
        content={tiktokPostContent}
        userId={getUserId()}
        onPostSuccess={() => {
          fetchData();
          setTiktokModalOpen(false);
          setTiktokPostContent(null);
        }}
      />
    </div>
  );
}
